import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import { logger } from '@tribeplatform/react-components/common'

import { usePingSession } from './hooks/index.js'
import {
  ActorAnalyticsContext,
  SnowplowTracker,
  TargetAnalyticsContext,
} from './lib/tracker/snowplow.js'
import { useRegisterSnowplowScript } from './lib/tracker/useRegisterSnowplowScript.js'

const isSSR = () => typeof window === 'undefined'

function getLang() {
  if (isSSR()) {
    return undefined
  }

  if (Array.isArray(navigator?.languages)) {
    return navigator.languages[0]
  }

  return navigator?.language
}

const unsetContextValues = (context: Partial<TargetAnalyticsContext> = {}) => {
  return Object.keys(context).reduce((acc, key: string) => {
    acc[key] = undefined
    return acc
  }, {})
}

export type TrackerConfig = {
  memberId?: string
  memberRoleId?: string
  memberRoleType?: string
  memberLocale?: string
  networkId?: string
  organizationId?: string
  appVersion?: string
  releaseChannel?: string
  snowplowCollectorAddress?: string
  enablePagePing?: boolean
  enablePageView?: boolean
}

export type TrackerContextType = {
  config: TrackerConfig
  unsetTracker: (target?: Partial<TargetAnalyticsContext>) => void
  tracker: SnowplowTracker
}

export const TrackerContext = createContext<TrackerContextType>({
  config: null,
  unsetTracker: () => null,
  tracker: null,
})

export interface TrackerProviderProps {
  config: TrackerConfig
  children: ReactNode
}

export const TrackerProvider = ({ config, children }: TrackerProviderProps) => {
  const {
    memberId,
    memberLocale,
    memberRoleId,
    memberRoleType,
    networkId,
    organizationId,
    enablePagePing,
    enablePageView,
    appVersion,
    releaseChannel,
    snowplowCollectorAddress,
  } = config ?? {}

  usePingSession({
    useQueryOptions: {
      enabled: !isSSR(),
      onError: error => {
        logger.warn('Ping session error', error)
      },
    },
  })

  const targetDefaultContext: TargetAnalyticsContext = useMemo(
    () => ({
      organizationId: organizationId || '',
      networkId: networkId || '',
    }),
    [networkId, organizationId],
  )

  const actorDefaultContext: ActorAnalyticsContext = useMemo(
    () => ({
      id: memberId || '',
      roleId: memberRoleId || '',
      roleType: memberRoleType || '',
      locale: memberLocale || getLang(),
    }),
    [memberId, memberLocale, memberRoleId, memberRoleType],
  )

  const tracker = useMemo(() => {
    const snowplowTracker = new SnowplowTracker({
      trackerName: 'tribe-analytics',
      enablePagePing: !!enablePagePing,
      enablePageView: !!enablePageView,
      appVersion,
      releaseChannel,
      snowplowCollectorAddress,
    })
    snowplowTracker.setTarget(targetDefaultContext)
    snowplowTracker.setActor(actorDefaultContext)

    return snowplowTracker
  }, [
    actorDefaultContext,
    appVersion,
    enablePagePing,
    enablePageView,
    releaseChannel,
    snowplowCollectorAddress,
    targetDefaultContext,
  ])

  useRegisterSnowplowScript({ networkId, tracker })

  const unsetTracker = useCallback(
    (target?: Partial<TargetAnalyticsContext>) => {
      tracker.setTarget(unsetContextValues(target))
    },
    [tracker],
  )

  const value = useMemo(
    () => ({ config, unsetTracker, tracker }),
    [config, unsetTracker, tracker],
  )

  return (
    <TrackerContext.Provider value={value}>{children}</TrackerContext.Provider>
  )
}

export const useTracker = () => {
  const context = useContext(TrackerContext)
  if (!context) {
    throw new Error(
      'useAnalyticsClient must be used within a AnalyticsClientProvider',
    )
  }
  return context
}
